import type { jsPDF } from "jspdf";
import {
  buildChartRenderPlan,
  getLayerChart,
  hexToRgb,
  normRectToPdfMm,
  type SurfLayer,
  type SurfStyle,
} from "@vida/surf-core";

export function drawChartLayer(
  pdf: jsPDF,
  layer: SurfLayer,
  theme: SurfStyle,
  rect: ReturnType<typeof normRectToPdfMm>,
): void {
  const plan = buildChartRenderPlan(getLayerChart(layer), theme);

  const mapX = (n: number) => rect.x + n * rect.w;
  const mapY = (n: number) => rect.y + n * rect.h;
  const mapW = (n: number) => n * rect.w;
  const mapH = (n: number) => n * rect.h;

  for (const poly of plan.polygons) {
    if (poly.points.length < 3) continue;
    const [r, g, b] = hexToRgb(poly.fill);
    pdf.setFillColor(r, g, b);
    const origin = poly.points[0]!;
    for (let i = 1; i < poly.points.length - 1; i += 1) {
      const a = poly.points[i]!;
      const bpt = poly.points[i + 1]!;
      pdf.triangle(
        mapX(origin.x),
        mapY(origin.y),
        mapX(a.x),
        mapY(a.y),
        mapX(bpt.x),
        mapY(bpt.y),
        "F",
      );
    }
  }

  for (const box of plan.rects) {
    const [r, g, b] = hexToRgb(box.fill);
    pdf.setFillColor(r, g, b);
    pdf.rect(mapX(box.x), mapY(box.y), mapW(box.w), mapH(box.h), "F");
  }

  for (const line of plan.lines) {
    const [r, g, b] = hexToRgb(line.stroke);
    pdf.setDrawColor(r, g, b);
    pdf.setLineWidth((line.width ?? 1) * 0.15);
    pdf.line(mapX(line.x1), mapY(line.y1), mapX(line.x2), mapY(line.y2));
  }

  for (const label of plan.labels) {
    const [r, g, b] = hexToRgb(label.fill);
    pdf.setTextColor(r, g, b);
    pdf.setFontSize(Math.max(6, (label.size ?? 0.03) * rect.h * 2.5));
    const align =
      label.anchor === "middle" ? "center" : label.anchor === "end" ? "right" : "left";
    pdf.text(label.text, mapX(label.x), mapY(label.y), {
      align,
      baseline: "middle",
    });
  }

  void layer.rotation;
}
